'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';

interface NonProdLoginGateProps {
  locale: string;
}

export default function NonProdLoginGate({ locale }: NonProdLoginGateProps) {
  const router = useRouter();
  const translate = useTranslations('non-prod-login');
  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setPending(true);

    try {
      const res = await fetch(`/${locale}/config/api/nonprod-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginId, loginPassword }),
      });

      if (res.ok) {
        router.refresh();
      } else {
        setError(translate('invalid-credentials'));
      }
    } catch {
      setError(translate('generic-error'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm rounded-lg bg-white p-24 shadow-md">
        <p className="global-text-xl-semi text-primary">{translate('title')}</p>
        <p className="global-text-sm mb-10 text-gray-600">{translate('subtitle')}</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-16">
            <label htmlFor="nonprod-login-id" className="global-text-sm-medium mb-6 block text-primary">
              {translate('login-id')}
            </label>
            <input
              id="nonprod-login-id"
              type="text"
              autoComplete="username"
              required
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              className="box-border w-full rounded-md border border-gray-300 px-12 py-10 text-sm text-black outline-none"
            />
          </div>

          <div className="mb-24">
            <label htmlFor="nonprod-login-password" className="global-text-sm-medium mb-6 block text-primary">
              {translate('password')}
            </label>
            <div className="relative">
              <input
                id="nonprod-login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="box-border w-full rounded-md border border-gray-300 px-12 py-10 pr-40 text-sm text-black outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? translate('hide-password') : translate('show-password')}
              >
                {showPassword ? <EyeSlashIcon className="size-15" /> : <EyeIcon className="size-15" />}
              </button>
            </div>
          </div>

          {error && (
            <p
              role="alert"
              className="mb-16 rounded-md border border-red-200 bg-red-50 px-12 py-10 text-sm text-red-600"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            className={`w-full rounded-md border-0 p-11 text-sm font-semibold text-white ${
              pending ? 'cursor-not-allowed bg-gray-400' : 'cursor-pointer bg-primary'
            }`}
          >
            {pending ? translate('verifying') : translate('sign-in')}
          </button>
        </form>
      </div>
    </div>
  );
}
